import React, {useEffect, useState, useMemo, useRef} from 'react';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {ReportContent} from '../modules/Report';
import {SchoolService} from '../modules/Schools';
import {setSchool} from '../redux/actions/user';

import {setSelectedClass} from '../redux/actions/class';
import SimpleLoader from '../components/SimpleLoader/SimpleLoader';


// Memoize ReportContent to avoid unnecessary re-renders when its props haven't changed
const MemoizedReportContent = React.memo(ReportContent);

export const ClassHomePage = () => {{
  const dispatch = useDispatch();
  const {schoolId} = useParams<{schoolId: string}>();
  // Track first render to set default class only once
  const isInitialRender = useRef(true);
  // Reference for canceling pending animation frames
  const animationFrameRef = useRef<number | null>(null);

  // ================= Redux state =================
  const teacher = useSelector((state: any) => state.user?.teacher);
  const classes = useSelector((state: any) => state.user?.classes);
  const school = useSelector((state: any) => state.user?.school);
  // Listen to Redux selectedClassId for sync with ClassControl
  const selectedClassIdFromRedux = useSelector(
    (state: any) => state.classState?.selectedClassId,
  );


  // ================= Component state =================
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  // loading: data fetch; uiLoading: immediate spinner
  const [loading, setLoading] = useState<boolean>(false);
  const [activeMainTab, setActiveMainTab] = useState<
    'report' | 'exercises'
  >('report');

  // ================= Services =================
  // useMemo ensures service instances are stable across renders
  const schoolService = useMemo(() => new SchoolService(), []);

  // ================= Sync local state with Redux (for ClassControl changes) =================
  useEffect(() => {
    if (
      selectedClassIdFromRedux &&
      selectedClassIdFromRedux !== selectedClassId
    ) {
      setSelectedClassId(selectedClassIdFromRedux);
    }
  }, [selectedClassIdFromRedux, selectedClassId]);

  // ================= Default class selection & initial spinner =================
  useEffect(() => {
    if (classes?.length > 0 && !selectedClassId && isInitialRender.current) {
      isInitialRender.current = false;

      const classIdToUse = selectedClassIdFromRedux || classes[0].id;

      setSelectedClassId(classIdToUse);

      // Only update Redux if it wasn't already set
      if (!selectedClassIdFromRedux) {
        dispatch(setSelectedClass(classIdToUse));
      }
    }
  }, [classes, selectedClassId, selectedClassIdFromRedux, dispatch]);





  // Clean up any RAF on unmount
  useEffect(
    () => () => {
      if (animationFrameRef.current !== null)
        cancelAnimationFrame(animationFrameRef.current);
    },
    [],
  );

  // ================= Effect: Load school details if needed =================
  useEffect(() => {
    if (schoolId && !school) {
      setLoading(true);
      schoolService
        .getSchoolById(schoolId)
        .then((resp: any) =>
          dispatch(setSchool(resp, teacher, resp.classesschool)),
        )
        .catch((error) => console.error('Error fetching school:', error))
        .finally(() => setLoading(false));
    }
  }, [schoolId, school, teacher, dispatch, schoolService]);

  // ================= Render =================
  return (
    <div>
      {/* Loading overlay */}
      {(loading) && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <SimpleLoader isLoading={true} title="Switching..." />
        </div>
      )}
      {/* Render ReportContent - tabs are now inside ClassReportStatsBar */}
      {selectedClassId && (
        <MemoizedReportContent
          classId={selectedClassId}
          selectedMonth={moment().format('YYYY-MM')}
          monthIndex={0}
          activeTab={activeMainTab}
          onTabChange={setActiveMainTab}
        />
      )}
    </div>
  );
};

}
